/**
 * Page "Emplois du temps des examens" — étudiant.
 * Affiche les sessions visibles + créneaux concernant son niveau/option.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, ChevronRight, ArrowLeft, MapPin, AlertCircle, Check } from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi, EmploiTempsSession, CreneauExamen } from '../services/portailApi';

function fmtDateRange(d1: string | null, d2: string | null): string {
  if (!d1 || !d2) return '';
  try {
    const a = new Date(d1).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    const b = new Date(d2).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    return `${a} → ${b}`;
  } catch { return ''; }
}

function groupByDate(creneaux: CreneauExamen[]): Map<string, CreneauExamen[]> {
  const map = new Map<string, CreneauExamen[]>();
  for (const c of creneaux) {
    const key = c.date || '';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(c);
  }
  return map;
}

const SessionDetail: React.FC<{ session: EmploiTempsSession; onBack: () => void }> = ({ session, onBack }) => {
  const grouped = groupByDate(session.creneaux);
  return (
    <div className="space-y-3">
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-blue-300 hover:text-blue-200">
        <ArrowLeft size={16} /> Retour à la liste
      </button>

      <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
        {/* Nom session : peut être très long avec des underscores type
            'Examen_Partiel_Session_Ordinaire_sem_pairs_aout_2026' → forcer
            le retour à la ligne sur les caractères non-espaces + les tirets/underscores. */}
        <h2 className="font-semibold text-gray-100 text-base leading-tight break-words">
          {session.nom.replace(/_/g, ' ')}
        </h2>
        <div className="text-xs text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 min-w-0">
            <Calendar size={12} className="flex-shrink-0" />
            <span className="truncate">{fmtDateRange(session.date_debut, session.date_fin)}</span>
          </span>
          <span>·</span>
          <span>S{session.semestre}</span>
          <span>·</span>
          <span className="px-2 py-0.5 rounded bg-blue-900/40 text-blue-300">{session.type_session_label}</span>
        </div>
      </div>

      {session.creneaux.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 text-center text-sm text-gray-400">
          Aucun examen trouvé pour votre option dans cette session.
        </div>
      ) : (
        <div className="space-y-3">
          {Array.from(grouped.entries()).map(([date, creneaux]) => (
            <div key={date} className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="bg-blue-900/30 px-4 py-2 text-sm font-semibold text-blue-200 uppercase tracking-wide flex items-center gap-2">
                <Calendar size={14} />
                {creneaux[0]?.jour_label || date}
              </div>
              <div className="divide-y divide-gray-800">
                {creneaux.map((c) => (
                  <div key={c.id} className="p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-100 text-sm">{c.matiere_nom}</div>
                        {c.enseignant_noms.length > 0 && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            {c.enseignant_noms.join(' · ')}
                          </div>
                        )}
                        {c.commentaire && (
                          <div className="text-xs text-yellow-400 mt-0.5 italic inline-flex items-center gap-1"><AlertCircle size={11} /> {c.commentaire}</div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-900/40 text-green-300 font-mono">
                          <Clock size={12} />
                          {c.heure_debut}–{c.heure_fin}
                        </div>
                        {c.groupe_label && (
                          <div className="text-[10px] text-gray-500 mt-1">{c.groupe_label}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function EmploisTempsExamens() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<EmploiTempsSession[]>([]);
  const [selected, setSelected] = useState<EmploiTempsSession | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    portailApi.getEmploisTemps()
      .then((res) => {
        if (mounted) {
          setSessions(res.sessions);
        }
      })
      .catch((e: any) => {
        if (e.status === 401) {
          portailApi.clearToken();
          navigate('/portail/login', { replace: true });
        } else {
          setError(e.message || 'Erreur de chargement');
        }
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [navigate]);

  return (
    <PortailLayout showHeader title="Emplois du temps">
      <div className="space-y-3 mt-2">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : error ? (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 text-sm text-red-300">
            {error}
          </div>
        ) : selected ? (
          <SessionDetail session={selected} onBack={() => setSelected(null)} />
        ) : sessions.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center">
            <Calendar size={40} className="mx-auto mb-3 text-gray-600" />
            <p className="text-gray-300 font-medium">Aucun emploi du temps disponible</p>
            <p className="text-sm text-gray-500 mt-2">
              Les emplois du temps des examens seront publiés ici par la scolarité.
            </p>
          </div>
        ) : (
          <>
            <div className="text-xs text-gray-400 mb-1">
              {sessions.length} session{sessions.length > 1 ? 's' : ''} disponible{sessions.length > 1 ? 's' : ''}
            </div>
            {sessions.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                className="w-full text-left bg-gray-900 hover:bg-gray-800 border border-gray-800 rounded-xl p-4 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <Calendar size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
                      {/* Nom peut être très long avec underscores. On le
                          rend display-friendly (underscore → espace) et on
                          autorise le retour à la ligne — évite le scroll
                          horizontal sur mobile. */}
                      <h3 className="font-semibold text-gray-100 text-sm leading-tight break-words min-w-0">
                        {s.nom.replace(/_/g, ' ')}
                      </h3>
                    </div>
                    <div className="text-xs text-gray-400 mt-1.5 flex items-center gap-2 flex-wrap">
                      <span>{fmtDateRange(s.date_debut, s.date_fin)}</span>
                      <span>·</span>
                      <span className="px-1.5 py-0.5 rounded bg-blue-900/40 text-blue-300 text-[10px]">
                        {s.type_session_label}
                      </span>
                      <span>·</span>
                      <span>S{s.semestre}</span>
                    </div>
                    <div className="mt-2 text-xs">
                      {s.concerne_etudiant ? (
                        <span className="inline-flex items-center gap-1 text-green-300">
                          <Check size={11} className="inline mr-0.5" /> {s.nombre_creneaux_etudiant} examen{s.nombre_creneaux_etudiant > 1 ? 's' : ''} pour vous
                        </span>
                      ) : (
                        <span className="text-gray-500 italic">Aucun examen pour votre option</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-gray-500 flex-shrink-0 mt-1" />
                </div>
              </button>
            ))}
          </>
        )}

        {!selected && (
          <button
            onClick={() => navigate('/portail/accueil')}
            className="w-full mt-4 py-3 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium"
          >
            ← Retour à l'accueil
          </button>
        )}
      </div>
    </PortailLayout>
  );
}
